/**
 * PublicPortalFrame.tsx
 * ----------------
 * UI frame for public portal single page app.
 */

import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { push } from 'connected-react-router';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { PublicPortalEvent, PublicPortalHistory, PublicPortalStatus, PublicPortalViewPivot } from '../../constants/ui';
import { useImmutableSelector } from '../../hooks/useImmutableSelector';
import {
    selectFilteredGeographies, selectFilteredServices, selectFilteredSeverities, selectFilteredTime,
} from '../../selectors/shared/history';
import { buildHistorySearch, getSearchString } from '../../utilities/urls';
import { IStyle } from '@fluentui/react/lib/Styling';

/**
 * Component properties
 */

interface PublicPortalViewProps {
    pivot: PublicPortalViewPivot;
}

/**
 * Private functions
 */

function getViewTitle (pivot: PublicPortalViewPivot) {
    if (pivot === 'event') {
        return 'Live Site Event Details';
    }

    return 'Visual Studio Service Status';
}

/**
 * UI frame for public portal single page app.
 * @param props Component properties.
 * @returns Rendered content.
 */
export function PublicPortalView (props: React.PropsWithChildren<PublicPortalViewProps>) {
    const { children, pivot } = props;

    // Action hooks
    const dispatch = useDispatch();

    // Application state hooks
    const filteredGeographies = useImmutableSelector(selectFilteredGeographies);
    const filteredServices = useImmutableSelector(selectFilteredServices);
    const filteredSeverities = useImmutableSelector(selectFilteredSeverities);
    const filteredTime = useImmutableSelector(selectFilteredTime);

    // Callback hooks
    const onPivotItemChanged = React.useCallback((item?: PivotItem) => {
        // Do nothing if item or itemKey is undefined, but log a warning
        if (item === undefined) {
            console.warn('Unknown pivot item selected');
            return;
        }

        if (item.props.itemKey === undefined) {
            console.warn('Pivot item with undefined itemKey was selected');
            return;
        }

        // When navigating to the History tab, add the prior search string back to the location
        if (item.props.itemKey === 'history') {
            dispatch(push({
                pathname: `/${item.props.itemKey}`,
                search: getSearchString(
                    buildHistorySearch(filteredGeographies, filteredServices, filteredSeverities, filteredTime)
                ),
            }));
            return;
        }

        dispatch(push(`/${item.props.itemKey}`));
    }, [dispatch, filteredGeographies, filteredServices, filteredSeverities, filteredTime]);

    const linkContent: IStyle = {
        fontWeight: 700
    };

    return (
        <div className="public-portal-view" id="main" role="main">
            <div className="public-portal-view__header-container">
                <div className="ms-Grid public-portal-view__grid" dir="ltr">
                    <h1 className="public-portal-view__header ms-Grid-col ms-sm12">
                        {getViewTitle(pivot)}
                    </h1>
                </div>
            </div>

            <div className="public-portal-view__body-container">
                {pivot !== PublicPortalEvent &&
                    <Pivot className="public-portal-view__pivot" onLinkClick={onPivotItemChanged} selectedKey={pivot} styles={{ linkContent }}>
                        <PivotItem headerText="Service Status" itemKey={PublicPortalStatus}>
                            {children}
                        </PivotItem>

                        <PivotItem headerText="History" itemKey={PublicPortalHistory}>
                            <PublicPortalViewFrame>
                                {children}
                            </PublicPortalViewFrame>
                        </PivotItem>
                    </Pivot>}

                {pivot === PublicPortalEvent &&
                    <PublicPortalViewFrame>
                        {children}
                    </PublicPortalViewFrame>}
            </div>
        </div>
    );
}

function PublicPortalViewFrame (props: React.PropsWithChildren<{}>) {
    const { children } = props;

    return (
        <div className="public-portal-view__frame">
            <div className="ms-Grid public-portal-view__grid" dir="ltr">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
